import { useNavigate } from '@tanstack/react-router';
import type { BestLocation } from '@imza/shared';
import { Skeleton } from '../ui/Skeleton.js';

interface Props {
  locations: BestLocation[];
  isLoading: boolean;
}

export function BestLocationsList({ locations, isLoading }: Props) {
  const navigate = useNavigate();

  const max = locations.length > 0 ? Math.max(...locations.map((l) => l.untouched)) : 1;

  return (
    <div className="bg-white border border-border rounded-xl p-4">
      <p className="text-sm font-semibold text-primary-text mb-3">Best locations by availability</p>
      {isLoading ? (
        <div className="space-y-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-9 rounded" style={{ width: `${55 + i * 9}%` }} />
          ))}
        </div>
      ) : locations.length === 0 ? (
        <p className="text-sm text-secondary text-center py-4">No untouched locations found</p>
      ) : (
        <ul className="space-y-1.5">
          {locations.map((loc) => {
            const pct = max > 0 ? Math.round((loc.untouched / max) * 100) : 0;
            return (
              <li key={loc.id}>
                <button
                  onClick={() => navigate({ to: '/landlords' as any, search: { area_id: loc.id } as any })}
                  className="w-full text-left group min-h-[44px] flex flex-col justify-center"
                  aria-label={`Browse ${loc.name} — ${loc.untouched.toLocaleString()} untouched`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-sm font-medium text-primary-text group-hover:text-primary transition-colors truncate pr-2">
                      {loc.name}
                    </span>
                    <span className="text-xs text-secondary tabular-nums flex-shrink-0">
                      {loc.untouched.toLocaleString()}
                      <span className="text-secondary/60"> / {loc.total.toLocaleString()}</span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-border rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary/70 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
